/** @file Handles the OTP form for new login and registration. */
import { identity } from 'purify-ts';
import p from 'vue-strict-prop';
import * as tsx from 'vue-tsx-support';
import { Maybe } from '@codegrade/maybe';
import * as form from '@/components/Form';
import * as utils from '@/utils';
import { clsInput } from './cls';
import { ConfirmEndingPreviousSession } from './ConfirmEndingPreviousSession';
const OTP_LENGTH = 8;
const OTP_REGEX = new RegExp(`^[0-9]{${OTP_LENGTH}}$`);
/**
 * Formats a raw OTP digit string for display, inserting a hyphen after the 4th digit.
 *
 * @param str The raw digit string.
 * @returns The display string (e.g. `"12345678"` → `"1234-5678"`).
 */
function getInputDisplayValue(str) {
    return str.length <= 4 ? str : `${str.slice(0, 4)}-${str.slice(4)}`;
}
/**
 * Strips all non-numeric characters and truncates to at most {@link OTP_LENGTH} digits.
 *
 * @param str The raw input string from the DOM event.
 * @returns A string of at most {@link OTP_LENGTH} digits.
 */
function getInputRealValue(str) {
    return str.replace(/\D/g, '').slice(0, OTP_LENGTH);
}
/** Controlled input for an OTP_LENGTH-digit one-time password with automatic xxxx-xxxx display formatting. */
export const OTPInput = tsx.componentFactoryOf().create({
    name: 'otp-input',
    props: {
        /** The current raw OTP value (digits only, without the display hyphen). */
        value: p(String).default(''),
        autofocus: p(Boolean).default(false),
        readOnly: p(Boolean).default(false),
        required: p(Boolean).default(false),
        fontSize: p.ofStringLiterals('text-inherit', 'text-4xl').default('text-4xl'),
    },
    methods: {
        /**
         * Strips non-numeric characters, caps at OTP_LENGTH digits, and emits the cleaned value.
         *
         * @param val The raw input string from the DOM event.
         */
        handleInput(val) {
            tsx.emitOn(this, 'onInput', getInputRealValue(val));
            // Force sync input value State
            // For the science behind this operation, see: ts/vue/src/components/Form/components/Choices/factory.tsx
            Maybe.fromNullable(this.$el)
                .chainNullable($el => $el instanceof HTMLInputElement ? $el : $el.querySelector('input'))
                .ifJust(input => {
                input.value = getInputDisplayValue(getInputRealValue(input.value));
            });
        },
    },
    /** @returns The rendered OTP input JSX. */
    render() {
        return (<form.Input v-focus={this.autofocus} class={[this.fontSize, clsInput]} type="text" autocomplete="one-time-code" value={getInputDisplayValue(this.value)} readOnly={this.readOnly} onInput={this.handleInput} required={this.required}/>);
    },
});
/** Form for entering and submitting an OTP_LENGTH-digit one-time password. */
export const OTPForm = tsx.component({
    name: 'one-time-code-form',
    props: {
        emailAddress: p.ofType().default(Maybe.empty),
        prefilled: p.ofType().required,
        submit: p.ofType()
            .required,
        autofocus: p(Boolean).default(true),
    },
    /** @returns State of the component. */
    data() {
        return {
            inputValue: this.prefilled.orDefault(''),
            hasAutoSubmitted: false,
            isolatedSessionNotified: false,
            terminateIsolatedSession: false,
        };
    },
    computed: {
        /** @returns The reason why the submit button is disabled, or `undefined` if it is not disabled. */
        submitDisabledReason() {
            if (!OTP_REGEX.test(this.inputValue)) {
                return { type: 'tooltip', reason: `Please enter a valid ${OTP_LENGTH}-digit code` };
            }
            if (this.isolatedSessionNotified && !this.terminateIsolatedSession) {
                return {
                    type: 'tooltip',
                    reason: 'To login, you must confirm that you want to terminate the isolated session.',
                };
            }
            return undefined;
        },
    },
    methods: {
        /**
         * Receives the cleaned OTP value from OTPInput and triggers auto-submit on completion.
         *
         * @param val The cleaned input value (digits only, max OTP_LENGTH long).
         */
        handleInput(val) {
            this.inputValue = val;
            if (!this.hasAutoSubmitted && val.length === OTP_LENGTH && this.prefilled.isNothing()) {
                this.hasAutoSubmitted = true;
                this.clickSubmit();
            }
        },
        /**
         * Find and click the submit button in the Form Root component.
         *
         * Fail silently.
         */
        async clickSubmit() {
            const maybeForm = await this.$withRef('form', identity);
            const maybeBtn = await maybeForm
                .filter(utils.isVueComponent)
                .chainPromise(formComp => formComp.$withRef('submitButton', utils.getRefAsHTMLElement));
            maybeBtn.filter(v => v != null).ifJust($btn => $btn.click());
        },
    },
    /** @returns The rendered OTP form JSX. */
    render() {
        return (<form.Root ref="form" handleSubmit={async () => await this.submit(this.inputValue, this.terminateIsolatedSession)} submitLoadingMessage="Submitting request" submitSuccessMessage="Received response" submitLabel="Submit" submitDisabledReason={this.submitDisabledReason} submitOnEnter actionBarVariant="wide-submit" onError={({ error: err }) => {
                if (utils.isApiErrorWithCode(err, 'ISOLATED_SESSION_ACTIVE')) {
                    this.isolatedSessionNotified = true;
                }
            }} scopedSlots={{
                formError: err => {
                    if (utils.isApiErrorWithCode(err, 'ISOLATED_SESSION_ACTIVE')) {
                        return (<ConfirmEndingPreviousSession value={this.terminateIsolatedSession} onInput={val => {
                                this.terminateIsolatedSession = val;
                            }}/>);
                    }
                    return utils.getErrorMessage(err);
                },
            }}>
                <p class="body-medium">
                    You will receive an email{' '}
                    {this.emailAddress.caseOf({
                Nothing: () => <span>if the provided email address</span>,
                Just: addr => (<span>
                                at <span class="font-medium">{addr}</span> if it
                            </span>),
            })}{' '}
                    connects to a CodeGrade account. Please check your email (including the spam
                    folder) for the one time password.
                </p>
                <form.Field>
                    <form.Label>One-time Password</form.Label>
                    <OTPInput value={this.inputValue} autofocus={this.autofocus} readOnly={this.prefilled.isJust()} onInput={this.handleInput} required/>
                </form.Field>
            </form.Root>);
    },
});
